"use client";

// Extracted from app/page.tsx (lines 78–91, 418–458).
// Owns: slideshow interval state, shuffle deck, timer effect.
// Accepts filteredData and exploreTabId so the pool memo re-derives on changes.

import { useState, useMemo, useRef, useEffect } from "react";
import { PanoramaFeatureCollection } from "@/api-client";
import { ExploreTabId, EXPLORE_TABS } from "@/shared/ui";

export const SLIDESHOW_INTERVALS = [
  { label: "30 seconds", shortLabel: "30s",    ms: 30_000  },
  { label: "1 minute",   shortLabel: "1 min",  ms: 60_000  },
  { label: "3 minutes",  shortLabel: "3 min",  ms: 180_000 },
  { label: "5 minutes",  shortLabel: "5 min",  ms: 300_000 },
  { label: "10 minutes", shortLabel: "10 min", ms: 600_000 },
] as const;

export type SlideshowMs = typeof SLIDESHOW_INTERVALS[number]["ms"];

export function useSlideshow(
  filteredData: PanoramaFeatureCollection | null,
  exploreTabId: ExploreTabId,
) {
  const [slideshowInterval, setSlideshowInterval] = useState<SlideshowMs | null>(null);
  const [slideshowMenuOpen,  setSlideshowMenuOpen]  = useState(false);
  const slideshowMenuRef  = useRef<HTMLDivElement>(null);
  const slideshowDeckRef  = useRef<string[]>([]);
  const slideshowPoolRef  = useRef<string[]>([]);

  // Pool: filtered panoramas sorted by the active explore tab
  const slideshowPool = useMemo(() => {
    if (!filteredData) return [];
    const def = EXPLORE_TABS.find((t) => t.id === exploreTabId) ?? EXPLORE_TABS[0];
    return [...filteredData.features].sort(def.sort);
  }, [filteredData, exploreTabId]);

  // Reset deck when pool changes so no stale IDs leak through
  useEffect(() => {
    const ids = slideshowPool.map((f) => String((f.properties as any).id));
    slideshowPoolRef.current = ids;
    slideshowDeckRef.current = [];
  }, [slideshowPool]);

  // Click-outside: close the slideshow dropdown
  useEffect(() => {
    if (!slideshowMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (slideshowMenuRef.current && !slideshowMenuRef.current.contains(e.target as Node)) {
        setSlideshowMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [slideshowMenuOpen]);

  // Timer: navigate to the next random panorama at the chosen interval
  useEffect(() => {
    if (!slideshowInterval) return;

    // Helper: pick the next photo from the shuffled deck
    const advance = () => {
      const pool = slideshowPoolRef.current;
      if (pool.length === 0) return;
      if (slideshowDeckRef.current.length === 0) {
        slideshowDeckRef.current = [...pool].sort(() => Math.random() - 0.5);
      }
      const nextId = slideshowDeckRef.current.pop()!;
      window.location.hash = `pano=${nextId}&zoom=30`;
    };

    // Navigate immediately on start, then on every interval tick
    advance();
    const timer = setInterval(advance, slideshowInterval);
    return () => clearInterval(timer);
  }, [slideshowInterval]);

  return {
    slideshowInterval, setSlideshowInterval,
    slideshowMenuOpen, setSlideshowMenuOpen,
    slideshowMenuRef,
    slideshowDeckRef,
    slideshowPool,
  };
}
